import React, { useEffect, useState } from "react";
import { MessageSquare, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const MSG = {
    toggle: "messages-toggle-btn",
    panel: "messages-panel",
    threadRow: "messages-thread-row",
    input: "messages-input",
    sendBtn: "messages-send-btn",
    backBtn: "messages-back-btn",
};

export default function MessagesDrawer() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [threads, setThreads] = useState([]);
    const [active, setActive] = useState(null); // { other_user_id, other_full_name }
    const [msgs, setMsgs] = useState([]);
    const [text, setText] = useState("");

    async function loadThreads() {
        try {
            const { data } = await api.get("/social/threads");
            setThreads(data);
        } catch { /* noop */ }
    }
    async function loadMessages(otherId) {
        try {
            const { data } = await api.get(`/social/messages/${otherId}`);
            setMsgs(data);
        } catch { /* noop */ }
    }

    useEffect(() => { if (open && user) loadThreads(); }, [open, user]);

    if (!user) return null;

    async function send(e) {
        e.preventDefault();
        if (!text.trim() || !active) return;
        try {
            await api.post("/social/messages", { to_user_id: active.other_user_id, body: text });
            setText("");
            await loadMessages(active.other_user_id);
            loadThreads();
        } catch (err) { /* toast handled elsewhere */ }
    }

    const unreadCount = threads.filter((t) => t.unread).length;

    return (
        <>
            <button
                data-testid={MSG.toggle}
                onClick={() => setOpen(!open)}
                className={`fixed bottom-24 right-6 z-30 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all ${
                    open ? "bg-stone-900 text-white" : "bg-white border-2 border-emerald-200 text-emerald-700 hover:scale-105"
                }`}
                aria-label="Messages"
            >
                {open ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
                {!open && unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">
                        {unreadCount}
                    </span>
                )}
            </button>

            {open && (
                <div
                    data-testid={MSG.panel}
                    className="fixed bottom-44 right-6 z-30 w-[360px] max-w-[calc(100vw-2rem)] h-[500px] bg-white rounded-3xl border border-stone-200 shadow-2xl flex flex-col overflow-hidden animate-fade-in"
                >
                    <div className="p-4 border-b border-stone-100 flex items-center gap-2">
                        {active && (
                            <button
                                data-testid={MSG.backBtn}
                                onClick={() => { setActive(null); setMsgs([]); }}
                                className="text-stone-500 hover:text-stone-800"
                            >
                                ←
                            </button>
                        )}
                        <MessageSquare className="w-5 h-5 text-emerald-700" />
                        <div className="font-outfit font-semibold text-stone-900">
                            {active ? active.other_full_name || "Conversation" : "Messages"}
                        </div>
                    </div>

                    {!active ? (
                        <div className="flex-1 overflow-y-auto">
                            {threads.length === 0 && (
                                <div className="p-6 text-sm text-stone-500 font-dmsans">
                                    No conversations yet. Messages between you and matched caregivers/families appear here.
                                </div>
                            )}
                            {threads.map((t) => (
                                <button
                                    key={t.thread_key}
                                    data-testid={MSG.threadRow}
                                    onClick={() => { setActive(t); loadMessages(t.other_user_id); }}
                                    className="w-full text-left p-4 border-b border-stone-100 hover:bg-stone-50 transition-colors"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="font-medium text-stone-900 text-sm">
                                            {t.other_full_name || "User"}
                                            <span className="text-xs text-stone-400 uppercase ml-2">{t.other_role}</span>
                                        </div>
                                        {t.unread && <span className="w-2 h-2 rounded-full bg-orange-500" />}
                                    </div>
                                    <div className="text-xs text-stone-500 mt-1 font-dmsans line-clamp-1">{t.last_message}</div>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <>
                            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-stone-50">
                                {msgs.map((m) => (
                                    <div
                                        key={m.id}
                                        className={`text-sm font-dmsans whitespace-pre-wrap ${
                                            m.from_user_id === user.id
                                                ? "ml-auto bg-emerald-700 text-white rounded-2xl rounded-br-sm px-4 py-2 max-w-[80%]"
                                                : "mr-auto bg-white border border-stone-200 text-stone-800 rounded-2xl rounded-bl-sm px-4 py-2 max-w-[80%]"
                                        }`}
                                    >
                                        {m.body}
                                    </div>
                                ))}
                            </div>
                            <form onSubmit={send} className="p-3 border-t border-stone-100 flex gap-2 bg-white">
                                <input
                                    data-testid={MSG.input}
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder="Type a message…"
                                    className="flex-1 h-10 rounded-full border border-stone-200 px-4 text-sm font-dmsans focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                />
                                <Button
                                    data-testid={MSG.sendBtn}
                                    type="submit"
                                    className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-10 w-10 p-0"
                                >
                                    <Send className="w-4 h-4" />
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            )}
        </>
    );
}
